import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useIntl } from 'react-intl'
import { getRouteApi } from '@tanstack/react-router'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { LockClosedIcon } from '@gto/ui/icons/outline'
import type { InputProps } from '@gto/ui/input'
import { colors } from '@gto/ui/theme/design-tokens'
import { Typography } from '@gto/ui/typography'

import { useAccountPasswordResetConfirmCreate } from '@/api/gen'
import { FormContainer } from '@/components/form-container'
import { TextInput } from '@/components/text-input'
import { useFormValidation } from '@/hooks/use-form-validation'
import { PasswordRulesChecker } from '@/pages/auth/components/password-rules-checker'
import { defaultAuthQuery, type PasswordResetModel } from '@/pages/auth/search-schema'
import { getAxiosErrorMessage } from '@/utils/helpers'
import type { UpdatePasswordFormData, UpdatePasswordFormField } from '@/utils/validation'
import { updatePasswordSchema } from '@/utils/validation'

const routeApi = getRouteApi('/_auth/login/password-reset/create-new')

type ResetStatus = {
  status?: PasswordResetModel['status']
  message?: string
}
export const RootRoute = () => {
  const navigate = routeApi.useNavigate()
  const { token, uidb64 } = routeApi.useSearch()

  const { formatMessage } = useIntl()
  const isSmAndLower = useMediaQuery('down', 'sm')

  const { validate, validateField, errors, clearFieldError, isValid } =
    useFormValidation(updatePasswordSchema)
  const [formValues, setFormValues] = useState<UpdatePasswordFormData>({
    new_password1: '',
    new_password2: '',
  })

  const [resetResult, setResetResult] = useState<ResetStatus | null>(null)

  const { mutate: updatePassword, isPending } = useAccountPasswordResetConfirmCreate({
    mutation: {
      onSuccess: () => {
        setResetResult({
          status: 'success',
          message: formatMessage({ id: 'auth.passwordUpdate.success.description' }),
        })
      },
      onError: (error: AxiosError) => {
        const err = getAxiosErrorMessage(error)
        setResetResult({
          status: 'error',
          message: err || formatMessage({ id: 'auth.passwordUpdate.error' }),
        })
      },
    },
  })

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))

    setTimeout(() => {
      const isValidField = validateField(name as UpdatePasswordFormField, value)

      if (isValidField) {
        clearFieldError(name as UpdatePasswordFormField)
      }
    }, 300)
  }

  const onSubmit = () => {
    setResetResult(null)
    const isValid = validate(formValues)
    const isPasswordsMatch = formValues.new_password1 === formValues.new_password2
    if (!isPasswordsMatch) {
      setResetResult({
        status: 'error',
        message: formatMessage({ id: 'auth.passwordUpdate.error.mismatch' }),
      })

      return
    }

    if (isValid) {
      setResetResult(null)

      updatePassword({ data: formValues, token, uidb64 })
    }
  }

  const getInputProps = (name: UpdatePasswordFormField): InputProps => ({
    disabled: isPending || resetResult?.status === 'success',
    type: 'password',
    size: 'medium',
    onChange,
    value: formValues[name],
    name,
    id: name,
  })

  return (
    <FormContainer>
      <div>
        <Typography
          gutterBottom
          className="flex items-start gap-2"
          variant={isSmAndLower ? 'h5' : 'h4'}
        >
          <LockClosedIcon
            color={colors.primary.main}
            height={36}
            style={{ flex: '0 0 auto' }}
          />{' '}
          <FormattedMessage
            id={
              resetResult?.status === 'success'
                ? 'auth.passwordUpdate.success.title'
                : 'auth.passwordUpdate.title'
            }
          />
        </Typography>
        {resetResult?.status !== 'success' && (
          <Typography
            className="whitespace-pre-wrap"
            color="text.secondary"
            variant="body1"
          >
            <FormattedMessage id="auth.passwordUpdate.description" />
          </Typography>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex gap-8 sm:flex-col sm:gap-6">
          <TextInput
            className="flex-[1_1_0px]"
            error={errors.new_password1}
            input={getInputProps('new_password1')}
            label="common.labels.password"
          />
          <TextInput
            className="flex-[1_1_0px]"
            error={errors.new_password2}
            input={getInputProps('new_password2')}
            label="common.labels.confirmPassword"
          />
        </div>
      </div>
      {resetResult && (
        <Alert
          heading={resetResult.message}
          severity={resetResult.status === 'success' ? 'success' : 'warning'}
        />
      )}
      <PasswordRulesChecker password={formValues.new_password1} />

      <Button
        color="primary"
        disabled={isPending || !isValid}
        loading={isPending}
        size="large"
        variant="contained"
        onClick={
          resetResult?.status === 'success'
            ? () =>
                navigate({
                  to: '/login',
                  viewTransition: true,
                  replace: true,
                  search: defaultAuthQuery,
                }).catch(console.error)
            : onSubmit
        }
      >
        <FormattedMessage
          id={
            resetResult?.status === 'success'
              ? 'common.controls.backToLogin'
              : 'common.controls.updatePassword'
          }
        />
      </Button>
    </FormContainer>
  )
}
